// src/schema.ts
var notificationSchema = {
  version: 0,
  primaryKey: "id",
  type: "object",
  properties: {
    id: { type: "string", maxLength: 100 },
    title: { type: "string" },
    body: { type: "string" },
    type: { type: "string" },
    read: { type: "boolean" },
    dismissed: { type: "boolean" },
    actionUrl: { type: "string" },
    metadata: { type: "object" },
    createdAt: { type: "number" },
    readAt: { type: "number" }
  },
  required: ["id", "title", "read", "dismissed", "createdAt"],
  indexes: ["read", "dismissed", "createdAt"]
};

// src/notifications.ts
var SimpleObservable = class {
  constructor() {
    this._subscribers = /* @__PURE__ */ new Set();
    this._hasValue = false;
  }
  next(value) {
    this._lastValue = value;
    this._hasValue = true;
    for (const fn of [...this._subscribers]) {
      try {
        fn(value);
      } catch {
      }
    }
  }
  subscribe(fn) {
    this._subscribers.add(fn);
    if (this._hasValue) {
      try {
        fn(this._lastValue);
      } catch {
      }
    }
    return () => this._subscribers.delete(fn);
  }
  dispose() {
    this._subscribers.clear();
  }
};
function createNotificationManager(config) {
  const { collection, maxAge, maxCount } = config;
  const unreadCount$ = new SimpleObservable();
  const notifications$ = new SimpleObservable();
  let changeUnsub = null;
  async function refresh() {
    const [unread, visible] = await Promise.all([
      collection.count({ read: false, dismissed: false }),
      collection.find({
        selector: { dismissed: false },
        sort: [{ createdAt: "desc" }]
      }).exec()
    ]);
    unreadCount$.next(unread);
    notifications$.next(visible);
  }
  changeUnsub = collection.changes$.subscribe(() => {
    refresh();
  });
  refresh();
  async function evict() {
    if (maxAge) {
      const all = await collection.find().exec();
      const cutoff = Date.now() - maxAge;
      const old = all.filter((n) => n.createdAt < cutoff);
      for (const n of old) {
        await collection.delete(n.id);
      }
    }
    if (maxCount) {
      const all = await collection.find({ sort: [{ createdAt: "desc" }] }).exec();
      if (all.length > maxCount) {
        const sorted = [...all].sort((a, b) => {
          if (a.dismissed !== b.dismissed) return a.dismissed ? -1 : 1;
          if (a.read !== b.read) return a.read ? -1 : 1;
          return a.createdAt - b.createdAt;
        });
        const toRemove = sorted.slice(0, all.length - maxCount);
        for (const n of toRemove) {
          await collection.delete(n.id);
        }
      }
    }
  }
  const manager = {
    async add(input) {
      const notif = {
        id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `notif_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        title: input.title,
        body: input.body,
        type: input.type,
        actionUrl: input.actionUrl,
        metadata: input.metadata,
        read: false,
        dismissed: false,
        createdAt: Date.now()
      };
      await collection.insert(notif);
      await evict();
      return notif;
    },
    async markRead(id) {
      const doc = await collection.findOne(id);
      if (doc && !doc.read) {
        await collection.update({ ...doc, read: true, readAt: Date.now() });
      }
    },
    async markAllRead() {
      const unread = await collection.find({ selector: { read: false } }).exec();
      const now = Date.now();
      for (const doc of unread) {
        await collection.update({ ...doc, read: true, readAt: now });
      }
    },
    async dismiss(id) {
      const doc = await collection.findOne(id);
      if (doc && !doc.dismissed) {
        await collection.update({ ...doc, dismissed: true });
      }
    },
    async dismissAll() {
      const visible = await collection.find({ selector: { dismissed: false } }).exec();
      for (const doc of visible) {
        await collection.update({ ...doc, dismissed: true });
      }
    },
    async clear() {
      const all = await collection.find().exec();
      for (const doc of all) {
        await collection.delete(doc.id);
      }
    },
    async getById(id) {
      return collection.findOne(id);
    },
    async getUnread() {
      return collection.find({ selector: { read: false, dismissed: false } }).exec();
    },
    unreadCount$,
    notifications$,
    dispose() {
      if (changeUnsub) {
        changeUnsub();
        changeUnsub = null;
      }
      unreadCount$.dispose();
      notifications$.dispose();
    }
  };
  return manager;
}

// src/push.ts
var SimpleObservable2 = class {
  constructor() {
    this._subscribers = /* @__PURE__ */ new Set();
    this._hasValue = false;
  }
  next(value) {
    this._lastValue = value;
    this._hasValue = true;
    for (const fn of [...this._subscribers]) {
      try {
        fn(value);
      } catch {
      }
    }
  }
  subscribe(fn) {
    this._subscribers.add(fn);
    if (this._hasValue) {
      try {
        fn(this._lastValue);
      } catch {
      }
    }
    return () => this._subscribers.delete(fn);
  }
  dispose() {
    this._subscribers.clear();
  }
};
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const outputArray = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    outputArray[i] = raw.charCodeAt(i);
  }
  return outputArray;
}
function createPushManager(config) {
  let currentSubscription = null;
  const permission$ = new SimpleObservable2();
  function getPermission() {
    if (typeof Notification !== "undefined") return Notification.permission;
    return "default";
  }
  permission$.next(getPermission());
  const manager = {
    get permission() {
      return getPermission();
    },
    get subscription() {
      return currentSubscription;
    },
    permission$,
    isSupported() {
      return typeof PushManager !== "undefined" && typeof navigator !== "undefined" && "serviceWorker" in navigator;
    },
    async subscribe() {
      if (!manager.isSupported()) return null;
      const permission = await Notification.requestPermission();
      permission$.next(permission);
      if (permission !== "granted") {
        config.onPermissionDenied?.();
        return null;
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.applicationServerKey)
      });
      currentSubscription = subscription;
      await config.onSubscribe(subscription);
      return subscription;
    },
    async unsubscribe() {
      if (!currentSubscription) return;
      const sub = currentSubscription;
      await sub.unsubscribe();
      currentSubscription = null;
      if (config.onUnsubscribe) {
        await config.onUnsubscribe(sub);
      }
    },
    dispose() {
      permission$.dispose();
      currentSubscription = null;
    }
  };
  return manager;
}

// src/registration.ts
function createPushRegistration(config) {
  const { client, endpoint, headers } = config;
  return {
    async onSubscribe(subscription) {
      await client.request({
        url: endpoint,
        method: "POST",
        headers,
        body: subscription.toJSON()
      });
    },
    async onUnsubscribe(subscription) {
      await client.request({
        url: endpoint,
        method: "DELETE",
        headers,
        body: { endpoint: subscription.endpoint }
      });
    }
  };
}

export { createNotificationManager, createPushManager, createPushRegistration, notificationSchema };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map